import $ from 'zepto';
import { expose } from '../main';
import { getStickyClasses, layoutSelector } from '../utils/constants/classes';
import { debounce } from '../utils';

const POSITION_TOP = 'top';
const POSITION_BOTTOM = 'bottom';
let $container;
let $col;
let $colLarge;
let $row;

const addMarginToBody = (position, bannersHeight) => {
  if (position === POSITION_TOP) {
    $('body').animate({ 'margin-top': bannersHeight }, 200);
  } else {
    $('body').animate({ 'margin-bottom': bannersHeight }, 200);
  }
};

const getDefaultPosition = ($banner, prefix) => ($banner.hasClass(`${prefix}sticky-banner-top`) ? POSITION_TOP : POSITION_BOTTOM);

const alignBanners = ($banners, position, duration = 200) => {
  // Get all banners based on position
  const $positionedBanners = $banners.filter(`[data-position="${position}"]`);
  // Sort the banners based on priority
  const bannersSortedByPriority = $positionedBanners.sort((a, b) => a.dataset.priority - b.dataset.priority);

  let bannersHeight = 0;
  let showPageContent = false;
  bannersSortedByPriority.each((index, element) => {
    if (position === POSITION_TOP) {
      $(element).css({ bottom: 'auto' }).animate({ top: bannersHeight }, duration);
    } else {
      $(element).animate({ bottom: bannersHeight }, duration);
    }
    // Sets showPageContent to true, to be applied to all the banners based on position
    if ($(element).data('show-page-content')) {
      showPageContent = true;
    }
    bannersHeight += $(element).height();
  });
  if (showPageContent) {
    // Show the page content behind the banner by adding margin to body
    addMarginToBody(position, bannersHeight);
  }
};

const alignAllBanners = ($banners, duration) => {
  alignBanners($banners, POSITION_TOP, duration);
  alignBanners($banners, POSITION_BOTTOM, duration);
};

const realignStickyBanners = ($banners, $banner) => {
  const position = $banner.data('position');
  if ($banners.length <= 1) {
    const showPageContent = !!$banner.data('show-page-content');
    if (position === POSITION_TOP) {
      $banner.css({ bottom: 'auto' }).animate({ top: 0 }, 200);
    }
    if (showPageContent) {
      // Show the page content behind the banner by adding margin to body
      addMarginToBody(position, $banner.height());
    }
    return false;
  }

  if (position === POSITION_TOP) {
    return alignBanners($banners, POSITION_TOP);
  }
  return alignBanners($banners, POSITION_BOTTOM);
};

const scrollToFocusElement = ($banner) => {
  const activeTop = $(document.activeElement)[0].getBoundingClientRect().top;
  const bannerTop = $banner[0].getBoundingClientRect().top;
  const bannersHeight = $banner.height();

  if ((bannerTop - bannersHeight) < activeTop) {
    const top = activeTop + window.scrollY;
    window.scroll({
      top,
      behavior: 'smooth',
    });
  }
};

const toggleStickyBanner = (limitTop, $banner) => {
  if (limitTop < window.innerHeight) {
    $banner.removeClass('xppvx_sticky-banner xppvx_sticky-banner-box-shadow-top');
    $col.removeClass('ppvx_col-sm-12 ppvx_col-md-8 ppvx_col-lg-6');
    $container.removeClass('ppvx_container');
    $row.removeClass('ppvx_row');
    $colLarge.removeClass('ppvx_col-sm-12 ppvx_col-md-8 ppvx_col-lg-8');

    $col.addClass('ppvx_col-12');
    $colLarge.addClass('ppvx_col-12');

    $(layoutSelector).css({ 'padding-bottom': 0 });
  }

  if ($banner[0].getBoundingClientRect().top + $banner.height() > window.innerHeight) {
    $banner.addClass('xppvx_sticky-banner xppvx_sticky-banner-box-shadow-top');
    $col.addClass('ppvx_col-sm-12 ppvx_col-md-8 ppvx_col-lg-6');
    $container.addClass('ppvx_container');
    $row.addClass('ppvx_row');
    $colLarge.addClass('ppvx_col-sm-12 ppvx_col-md-8 ppvx_col-lg-8');

    $col.removeClass('ppvx_col-12');
    $colLarge.removeClass('ppvx_col-12');

    $(layoutSelector).css({ 'padding-bottom': $banner.height() });
  }
};

const limitScrolling = (limitClassId, $banner, $banners) => {
  if ($banner.length > 0) {
    let bannersHeight = 0;
    $banners.forEach((item) => {
      bannersHeight += $(item).height();
    });

    const $limitComponent = $(`.${limitClassId}`);
    const limitTop = $limitComponent
      && $limitComponent[0]
      && $limitComponent[0].getBoundingClientRect().top;

    const limit = window.innerHeight - limitTop;
    if ($limitComponent.length > 0) {
      toggleStickyBanner(limitTop, $banner);
    }

    if (limit - bannersHeight + $banner.height() <= 0) {
      alignAllBanners($banners, 0);
    } else {
      $banner.css({ bottom: limit });
    }
  }
};

const setBannerAttributes = ($banner, display, prefix, limitClass) => {
  $banner.attr('data-priority', (display && display.priority) || 0);
  $banner.attr('data-position', (display && display.position) || getDefaultPosition($banner, prefix));
  $banner.attr('data-show-page-content', (display && display.showPageContent) || false);
  $banner.attr('data-limit', limitClass || false);
};

export default function initStickyBanner({
  elementId,
  callback,
  closeable = true,
  display,
  closeAnimationTimeout,
  limitClass,
  prefix = 'xvx-',
}) {
  const { stickyCloseElementSelector, stickyElementsSelector } = getStickyClasses(prefix);
  const $banner = $(`#${elementId}`);
  const $banners = $(`${stickyElementsSelector}`);
  $banner.hide();
  const onClickClose = () => {
    $banner.animate({
      opacity: 0,
    }, closeAnimationTimeout || 500, 'linear', () => {
      $banner.hide();
      realignStickyBanners($banners, $banner);

      if (callback) {
        callback();
      }
    });
  };

  setBannerAttributes($banner, display, prefix, limitClass);
  realignStickyBanners($banners, $banner);
  const $bannerWithDataLimit = $('[data-limit]:not([data-limit="false"])');
  $colLarge = $bannerWithDataLimit.find('.ppvx_col-lg-8');
  $container = $bannerWithDataLimit.find('.ppvx_container');
  $col = $bannerWithDataLimit.find('.ppvx_col-sm-12');
  $row = $bannerWithDataLimit.find('.ppvx_row');

  if (closeable) {
    $banner.find(stickyCloseElementSelector).on('click', onClickClose);
  } else {
    $banner.find(stickyCloseElementSelector).hide();
  }

  $(window).on('resize', () => {
    debounce(alignAllBanners($banners), 300);
    if (limitClass) {
      limitScrolling(limitClass, $bannerWithDataLimit, $banners);
      scrollToFocusElement($banner);
    }
  });

  $banner.show();
  if (limitClass) {
    $(layoutSelector).css({ 'padding-bottom': $banner.height() });
    alignAllBanners($banners);
    limitScrolling(limitClass, $bannerWithDataLimit, $banners);

    $(window).on('scroll', () => {
      debounce(limitScrolling(limitClass, $bannerWithDataLimit, $banners), 200);
    });

    $('input').on('focus', () => {
      scrollToFocusElement($banner);
    });
  }

  return onClickClose;
}

expose({ initStickyBanner });
